import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

// ── Collection (代收代付) cell — ONE rendering for Bank Statement and Petty Cash ────────────────
// Owner 2026-08-24: "在 Petty Cash 里,也需要支持一样的功能" — same total, same per-tax-family
// grouping, same per-tax Paid tick with its own editable date. Extracted from BankStatement so the
// two screens cannot drift; the server-side rule is already single-source (bankPassThrough.ts).
//
// Grouping is BY TAX, because each tax on the same payment is remitted on its own day
// (owner 2026-08-22). The raw lines behind a group stay visible so nothing that used to be shown
// is lost.

export type CollectionRow = {
  collection?: number;
  collectionDetail?: Array<{ name: string; amount: number; taxKey?: string }>;
  collectionPaid?: Array<{ taxKey: string; paidOn: string; byName?: string }>;
};

// ⚠️ This list is the FILTER as well as the order: taxGroupsOf drops any key not in it. It was
// missing 'pnd51', which the server's TAX_KEYS has produced since the half-year tax landed — so a
// PND.51 pass-through was computed, stored, and then silently never drawn. Keep it in step with
// bankPassThrough.TAX_KEYS.
const TAX_ORDER = ['pnd1', 'sso', 'wht', 'pnd3', 'pnd53', 'vat', 'pnd51', 'pnd50', 'pnd94', 'pnd9091', 'property_tax', 'income_tax', 'fines', 'other'];
const TAX_LABEL: Record<string, string> = {
  pnd1: 'Income Tax (PND.1)', sso: 'Social Security (SSO)', wht: 'Withholding Tax (WHT)',
  pnd3: 'Withholding Tax (PND.3)', pnd53: 'Withholding Tax (PND.53)',
  vat: 'VAT', pnd51: 'Half-year Tax (PND.51)', pnd50: 'Corporate Income Tax (PND.50)', pnd94: 'Personal Half-year (PND.94)', pnd9091: 'Personal Income Tax (PND.90/91)', property_tax: 'Property Tax', income_tax: 'Income Tax', fines: 'Fines', other: 'Other',
};

const money = (n: any) => (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const todayISO = () => new Date(Date.now() + 7 * 3600 * 1000).toISOString().slice(0, 10);   // Bangkok
// The pop-down's width: what the Bank Statement's Collection column was before it became one, so the
// groups, ticks and dates inside keep exactly the room they had in the cell.
const PANEL_W = 230;

export function taxGroupsOf(r: CollectionRow) {
  const by = new Map<string, { key: string; label: string; amount: number; lines: any[] }>();
  for (const d of (r.collectionDetail || [])) {
    const k = (d as any).taxKey || 'other';
    const g = by.get(k) || { key: k, label: TAX_LABEL[k] || k, amount: 0, lines: [] };
    g.amount = Math.round((g.amount + (Number(d.amount) || 0)) * 100) / 100;
    g.lines.push(d);
    by.set(k, g);
  }
  return TAX_ORDER.filter(k => by.has(k)).map(k => by.get(k)!);
}

export default function CollectionCell({ row, onSetPaid, lockedKeys, popDown }: {
  row: CollectionRow;
  /**
   * Persist one tax family's paid date ('' clears the tick). The caller owns optimistic state and
   * REJECTS when the write failed — a remittance date that did not reach the database must never
   * look saved. Owner 2026-09-08: a tick placed while the API was frozen silently rolled back
   * behind a dismissable alert, so the operator could not tell a saved date from a lost one.
   */
  onSetPaid: (taxKey: string, paidOn: string) => void | Promise<void>;
  /**
   * Tax families whose tick this cell does NOT own: a tax pot holds the fact and cascades it here
   * (taxPots.setPotRemitted → setPaidMark), so what shows is a MIRROR, not a second decision.
   * Owner 2026-09-16: left editable, the mirror could be cleared on its own — the pot then still
   * said "paid", the line said nothing, and nothing existed to re-sync them. Read-only here; the
   * pot's own tick is the one control.
   */
  lockedKeys?: string[];
  /**
   * Calem, report 2133118d: "make it a pop-down (similar to the paperclip detail in a specific job
   * for active jobs) … keeping the column at a nominal size for the amount". The cell keeps the
   * purple total alone, with a round toggle to its left — a green tick once every tax on the line is
   * ticked Paid, a grey dashed circle until then — and the groups, ticks, dates, locks and NOT SAVED /
   * retry open in a panel under the amount. Bank Statement first; Petty Cash too since report
   * 33249b9a ("Same as bank accounts"). Left off, everything is drawn in the cell.
   */
  popDown?: boolean;
}) {
  const { t } = useTranslation();
  const locked = (k: string) => (lockedKeys || []).includes(k);
  const [busy, setBusy] = useState('');                       // tax key currently being written
  const [failed, setFailed] = useState<Record<string, { paidOn: string; msg: string }>>({});
  const save = async (taxKey: string, paidOn: string) => {
    setBusy(taxKey);
    setFailed(f => { const n = { ...f }; delete n[taxKey]; return n; });
    try { await onSetPaid(taxKey, paidOn); }
    catch (e: any) { setFailed(f => ({ ...f, [taxKey]: { paidOn, msg: e?.message || 'Save failed' } })); }
    finally { setBusy(''); }
  };

  // ── The pop-down (popDown only) ──
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const anchorRef = useRef<HTMLSpanElement>(null);          // the toggle + the amount
  const btnRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // Under the amount with the right edges together, as the column is right-aligned; above it when the
  // window has no room below, the same flip as the statement's Match ▾ menu. Measured, not guessed:
  // the panel is as tall as the taxes the line carries.
  const place = useCallback(() => {
    const a = anchorRef.current?.getBoundingClientRect();
    const h = panelRef.current?.offsetHeight;
    if (!a || h == null) return;
    const top = a.bottom + 4 + h > window.innerHeight ? Math.max(8, a.top - h - 4) : a.bottom + 4;
    const left = Math.max(8, Math.min(a.right - PANEL_W, window.innerWidth - PANEL_W - 8));
    setPos(p => (p && p.left === left && p.top === top ? p : { left, top }));
  }, []);
  // Every render while open, not only the first: a tick's date or a NOT SAVED line changes its height.
  useLayoutEffect(() => { if (open) place(); });
  useEffect(() => {
    if (!open) return;
    // The toggle is not "outside" — its own click would otherwise close the panel and reopen it.
    const onDown = (e: MouseEvent) => {
      const n = e.target as Node;
      if (!panelRef.current?.contains(n) && !btnRef.current?.contains(n)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // The panel is fixed to the window while the statement scrolls under it: follow the row.
    window.addEventListener('scroll', place, true);
    window.addEventListener('resize', place);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', place, true);
      window.removeEventListener('resize', place);
    };
  }, [open, place]);

  if (!row.collection) return <span style={{ color: 'var(--text-tertiary)' }}>–</span>;
  const groups = taxGroupsOf(row);
  const total = <span style={{ color: '#7C3AED', fontWeight: 700, textAlign: 'right' }}>฿{money(row.collection)}</span>;
  const detail = (
    <>
      {groups.map((g, gi) => {
        const mark = (row.collectionPaid || []).find(m => m.taxKey === g.key);
        return (
          // In the panel the first group drops its dotted rule: there is no total above it to divide from.
          <div key={g.key} style={popDown && gi === 0 ? undefined : { borderTop: '1px dotted var(--border)', paddingTop: 3, marginTop: 3 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 10.5, lineHeight: 1.3 }}>
              <span style={{ textAlign: 'left', fontWeight: 700, color: 'var(--text-secondary)' }}>{g.label}</span>
              <span style={{ flexShrink: 0, fontWeight: 700 }}>{money(g.amount)}</span>
            </div>
            {g.lines.length > 1 && (
              <div style={{ fontSize: 9.5, color: 'var(--text-tertiary)', textAlign: 'left', lineHeight: 1.25 }}>
                {g.lines.map((d: any, i: number) => <div key={i}>· {d.name} {money(d.amount)}</div>)}
              </div>
            )}
            <div onClick={e => e.stopPropagation()} style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 4, fontSize: 10 }}>
              <label title={locked(g.key) ? t('bs_coll_paid_locked') : t('bs_coll_paid_hint')}
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, cursor: locked(g.key) ? 'default' : busy === g.key ? 'wait' : 'pointer', color: mark ? '#15803D' : 'var(--text-tertiary)', fontWeight: mark ? 700 : 400 }}>
                <input type="checkbox" checked={!!mark} disabled={locked(g.key) || busy === g.key} style={{ cursor: 'inherit' }}
                  onChange={e => save(g.key, e.target.checked ? todayISO() : '')} />
                {t('bs_coll_paid')}
                {/* The tick is a mirror of the pot's — say so, or a disabled checkbox just reads as broken. */}
                {locked(g.key) && <i className="ti ti-lock" style={{ fontSize: 9.5, opacity: .55 }} aria-hidden />}
              </label>
              {busy === g.key && <i className="ti ti-loader" style={{ fontSize: 11, color: 'var(--text-tertiary)' }} aria-hidden />}
              {/* Outside the label on purpose: inside it, a click landing next to the field toggled
                  the checkbox and wiped the recorded remittance date. */}
              {mark && (
                <input type="date" value={mark.paidOn} disabled={locked(g.key) || busy === g.key}
                  onChange={e => save(g.key, e.target.value)}
                  title={locked(g.key) ? t('bs_coll_paid_locked') : mark.byName ? `Marked by ${mark.byName}` : undefined}
                  style={{ fontSize: 10, padding: '0 2px', border: '1px solid var(--border)', borderRadius: 4, background: 'var(--bg-app)', color: '#15803D', fontWeight: 700 }} />
              )}
            </div>
            {failed[g.key] && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 5, fontSize: 9.5, color: '#B42318', fontWeight: 700 }}
                onClick={e => e.stopPropagation()} title={failed[g.key].msg}>
                <i className="ti ti-alert-triangle" style={{ fontSize: 11 }} aria-hidden />NOT SAVED
                <button onClick={() => save(g.key, failed[g.key].paidOn)}
                  style={{ fontSize: 9.5, fontWeight: 800, padding: '0 5px', borderRadius: 4, border: '1px solid #B42318', background: 'transparent', color: '#B42318', cursor: 'pointer' }}>retry</button>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
  if (!popDown) return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 2 }}>
      {total}
      {detail}
    </div>
  );
  const allPaid = groups.length > 0 && groups.every(g => (row.collectionPaid || []).some(m => m.taxKey === g.key));
  // A save that fails after the panel was closed must still show on the line: the tick has rolled
  // back, and the NOT SAVED + retry that says so is inside the panel.
  const unsaved = Object.keys(failed).length > 0;
  return (
    <span ref={anchorRef} style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
      {groups.length > 0 && (
        <button ref={btnRef} type="button" aria-expanded={open} title={t('bs_coll_detail_tip')}
          onClick={e => { e.stopPropagation(); setOpen(o => !o); }}
          style={{ width: 16, height: 16, flexShrink: 0, boxSizing: 'border-box', padding: 0, borderRadius: '50%', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            ...(unsaved ? { border: '1.5px solid #B42318', background: '#FEF3F2', color: '#B42318' }
              : allPaid ? { border: '1.5px solid #15803D', background: '#15803D', color: '#fff' }
              : { border: '1.5px dashed var(--text-tertiary)', background: 'transparent' }),
            boxShadow: open ? '0 0 0 2px rgba(124,58,237,0.25)' : 'none' }}>
          {unsaved ? <i className="ti ti-alert-triangle" style={{ fontSize: 10 }} aria-hidden />
            : allPaid ? <i className="ti ti-check" style={{ fontSize: 11 }} aria-hidden /> : null}
        </button>
      )}
      {total}
      {/* In <body>, not the cell, which clips whatever overflows it. React still bubbles a click in here
          up through the row, where it would open the transaction preview (Petty Cash: the edit form) —
          stopped at the panel. */}
      {open && groups.length > 0 && createPortal(
        <div ref={panelRef} onClick={e => e.stopPropagation()}
          style={{ position: 'fixed', left: pos?.left ?? 0, top: pos?.top ?? 0, visibility: pos ? 'visible' : 'hidden', zIndex: 11001, width: PANEL_W, boxSizing: 'border-box', padding: '7px 10px', background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 10, boxShadow: 'var(--shadow-md)', fontSize: 12.5, color: 'var(--text-primary)' }}>
          {detail}
        </div>,
        document.body,
      )}
    </span>
  );
}
